import React from "react";
import { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import ExamsDropdown from "component/Common/SwitchExamDropdown";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { HeartIcon } from "@heroicons/react/24/solid";
import { createUserDisplayPicture } from "helpers";
import { logout } from "service/auth";
import { history } from "helpers";
import { icons } from "helpers/images";
import CommonModal from "../CommonModal";
import CapsuleBox from "UI/CapsuleElement";
import {
  Bars3Icon,
  ChevronDownIcon,
  ArrowLeftStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import GlobalSearch from "../GlobalSearch";
import { useMediaQuery } from "react-responsive";

const NavBar = ({
  data,
  getAllMeta,
  setContainerTrigger,
  selectedFeature,
  setSelectedFeature,
}) => {
  const isSubscribed = data?.default_exam?.is_subscribed || false;
  const location = useLocation();
  const urlPath = location.pathname;
  const isXtraSmallScreen = useMediaQuery({ minWidth: 617 });
  const isMediumScreen = useMediaQuery({ minWidth: 825 });
  const isXtraMediumScreen = useMediaQuery({ minWidth: 881 });
  const isXtraLargeScreen = useMediaQuery({ minWidth: 1281 });
  const [loginOpen, setLoginOpen] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showNewsfeedPopup, setShowNewsfeedPopup] = useState(false);
  const [showMobileMenuPopup, setshowMobileMenuPopup] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const profileMenuRef = useRef(null);
  const profileMenuButtonRef = useRef(null);
  const newsFeedRef = useRef(null);
  const newsFeedButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const navFeaturePaths = ["choiceTable", "referral", "packages", "profile"];

  const profiles = [
    {
      name: "Profile",
      svg: icons.Profile,
      routeTo: "/portal/profile",
    },
    {
      name: "My Packages",
      svg: icons.Packages,
      routeTo: "/portal/profile",
    },
    {
      name: "Logout",
      svg: icons.LogoutIcon,
      routeTo: "/auth/login",
    },
  ];

  const navItems = [
    {
      type: "internal",
      to: "/portal/referral",
      key: "referral",
      label: "Refer & Earn",
    },
    {
      type: "internal",
      to: "/portal/packages",
      key: "packages",
      label: isSubscribed ? "Packages" : "Get a package",
    },
    {
      type: "external",
      to: "https://www.zynerd.com/contact-us",
      key: "support",
      icon: <SupportAgentIcon sx={{ color: "white", width: "24px" }} />,
      label: "Support",
    },
  ];

  const newsFeedItems = [
    {
      to: "https://blog.zynerd.com/news/",
      label: "News",
    },
    {
      to: "https://blog.zynerd.com",
      label: "Blogs",
    },
  ];

  const renderMobileMenuItems = () => {
    let mobileMenuItems = [...newsFeedItems, ...navItems];
    if (isXtraSmallScreen)
      mobileMenuItems = mobileMenuItems.filter((item) => {
        let itemToPop = isSubscribed ? "Support" : "Get a package";
        if (item.label !== itemToPop) return item;
      });
    if (isMediumScreen) mobileMenuItems.pop();
    if (isXtraMediumScreen) mobileMenuItems.pop();
    if (isXtraLargeScreen) return [];
    return mobileMenuItems;
  };

  const renderNavItems = () => {
    let itemToPush = isSubscribed ? "Support" : "Get a package";
    let navItemsToShow = [];
    if (isXtraSmallScreen)
      navItemsToShow = navItems.filter((item) => item.label === itemToPush);
    if (isMediumScreen)
      navItemsToShow = navItems.filter((item) => item.label !== "Refer & Earn");
    if (isXtraMediumScreen) return navItems;
    return navItemsToShow;
  };

  const handleProfileMenu = async (menu) => {
    if (menu === "Logout") {
      logout();
    } else {
      history.push({
        pathname: menu["routeTo"],
        state: {
          tab: menu["name"],
        },
      });
    }
    setShowProfilePopup(false);
  };

  const handleChoiceListSelect = () => {
    setContainerTrigger("choiceTable");
  };

  const findMenuPath = () => {
    return navFeaturePaths.find((item) => urlPath.includes(item)) || null;
  };

  const handleNewsfeedClickOutside = (event) => {
    console.log("click inside", newsFeedRef.current?.contains(event.target));
    if (
      newsFeedRef.current !== undefined &&
      !newsFeedRef.current?.contains(event.target)
    ) {
      setShowNewsfeedPopup(false);
    }
  };

  const handleNewsfeedEscKey = (event) => {
    if (event.key === "Escape") {
      if (newsFeedRef.current) {
        setShowNewsfeedPopup(false);
      }
    }
  };

  useEffect(() => {
    if (newsFeedRef.current && showNewsfeedPopup) {
      document.addEventListener("click", handleNewsfeedClickOutside);
      document.addEventListener("keydown", handleNewsfeedEscKey);
      return () => {
        document.removeEventListener("click", handleNewsfeedClickOutside);
        document.removeEventListener("keydown", handleNewsfeedEscKey);
      };
    }
  }, [showNewsfeedPopup]);

  const handleProfileClickOutside = (event) => {
    console.log("click inside", profileMenuRef.current?.contains(event.target));
    if (
      profileMenuRef.current !== undefined &&
      !profileMenuRef.current?.contains(event.target)
    ) {
      setShowProfilePopup(false);
    }
  };

  const handleProfileEscKey = (event) => {
    if (event.key === "Escape") {
      if (profileMenuRef.current) {
        setShowProfilePopup(false);
      }
    }
  };

  useEffect(() => {
    if (profileMenuRef.current && showProfilePopup) {
      document.addEventListener("click", handleProfileClickOutside);
      document.addEventListener("keydown", handleProfileEscKey);
      return () => {
        document.removeEventListener("click", handleProfileClickOutside);
        document.removeEventListener("keydown", handleProfileEscKey);
      };
    }
  }, [showProfilePopup]);

  const handleMobileMenuClickOutside = (event) => {
    console.log("click inside", mobileMenuRef.current?.contains(event.target));
    if (
      mobileMenuRef.current !== undefined &&
      !mobileMenuRef.current?.contains(event.target)
    ) {
      setshowMobileMenuPopup(false);
    }
  };

  const handleMobileMenuEscKey = (event) => {
    if (event.key === "Escape") {
      if (mobileMenuRef.current) {
        setshowMobileMenuPopup(false);
      }
    }
  };

  useEffect(() => {
    if (mobileMenuRef.current && showMobileMenuPopup) {
      document.addEventListener("click", handleMobileMenuClickOutside);
      document.addEventListener("keydown", handleMobileMenuEscKey);
      return () => {
        document.removeEventListener("click", handleMobileMenuClickOutside);
        document.removeEventListener("keydown", handleMobileMenuEscKey);
      };
    }
  }, [showMobileMenuPopup]);

  useEffect(() => {
    let match = findMenuPath();
    if (match) {
      setSelectedFeature(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPath]);

  return (
    <div className="w-full flex flex-col !gap-2 items-center">
      <div
        id="headerNavBar"
        className="w-full flex !gap-3 z-10 justify-between items-center"
      >
        <div className="flex items-center !gap-2 max-h-8">
          <ExamsDropdown
            dropdownData={data?.all_exams}
            examName={data?.default_exam?.name}
            examId={data?.default_exam?.id}
            callMetaApi={getAllMeta}
          />

          <CapsuleBox
            parentClass={`flex group items-center min-w-fit py-1 pl-1 pr-2 gap-2 min-h-fit ${
              selectedFeature === "choiceTable"
                ? "bg-red-200 hover:!bg-red"
                : "bg-white hover:!bg-red-50"
            } rounded-3xl cursor-pointer`}
            leadingIconContentClass="items-center"
            leadingIcon={
              <div
                className={`rounded-full w-6 h-6 mr-2 group-hover:!bg-white ${
                  selectedFeature === "choiceTable" ? "bg-white" : "bg-red-50"
                } flex items-center justify-center`}
              >
                <HeartIcon className="text-red-200 w-4 mt-[1px]" />
              </div>
            }
            content={
              <div
                className={`text-${
                  selectedFeature === "choiceTable" ? "white" : "red-200"
                } text-sm font-bold `}
              >
                My Choice Lists
              </div>
            }
            onCapsuleClick={handleChoiceListSelect}
          />
        </div>

        <div className="hidden lg:flex flex-1">
          <GlobalSearch
            globalSearchTerm={globalSearchTerm}
            setGlobalSearchTerm={setGlobalSearchTerm}
          />
        </div>

        <div className="flex items-center !gap-2 text-sm font-bold max-h-8">
          <div className="hidden 2lg:block relative">
            <div
              ref={newsFeedRef}
              className={`flex justify-center items-center px-3 !py-[6px] rounded-full gap-1 cursor-pointer ${
                showNewsfeedPopup
                  ? "bg-white text-primary"
                  : "hover:bg-grey-200 text-black"
              }`}
              onClick={() => setShowNewsfeedPopup((prev) => !prev)}
            >
              <p>Newsfeed</p>
              <ChevronDownIcon className="w-3 !mt-[2px] stroke-bold" />
            </div>

            {showNewsfeedPopup && (
              <div
                ref={newsFeedButtonRef}
                className="absolute top-full left-0 mt-2 bg-white shadow-lg rounded-lg border p-2 w-32 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                {newsFeedItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.to}
                    rel="noreferrer"
                    target="_blank"
                    className="block px-4 py-[6px] text-black hover:bg-grey-100 rounded-md"
                    onClick={() => setShowNewsfeedPopup(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="hidden 2sm:flex !gap-1">
            {renderNavItems().map((item, index) => {
              const isGetPackage = item.label === "Get a package";
              const isActive = selectedFeature === item.key;

              return item.type === "internal" ? (
                <NavLink
                  key={index}
                  to={item.to}
                  className={`!px-2 2sm:!px-3 py-[6px] rounded-3xl font-semibold !cursor-pointer ${
                    isGetPackage
                      ? `text-white bg-primary hover:bg-primary-dark`
                      : `text-black ${
                          isActive
                            ? "bg-white text-primary"
                            : "hover:bg-grey-200"
                        }`
                  }`}
                >
                  <label className="cursor-pointer">{item.label}</label>
                </NavLink>
              ) : (
                <a
                  key={index}
                  href={item.to}
                  rel="noreferrer"
                  target="_blank"
                  className="flex gap-2 py-1 pr-3 pl-2 rounded-full items-center hover:bg-secondary-dark bg-secondary cursor-pointer"
                >
                  {item.icon}
                  <label className="text-sm font-bold text-white cursor-pointer">
                    {item.label}
                  </label>
                </a>
              );
            })}
          </div>

          <div className="relative block 2lg:hidden">
            <button
              ref={menuButtonRef}
              onClick={() => setshowMobileMenuPopup((prev) => !prev)}
              className="p-[6px] border rounded-full bg-white"
            >
              <Bars3Icon className="w-5 stroke-[2px]" />
            </button>

            {showMobileMenuPopup && (
              <div
                className="absolute top-full right-full translate-x-[20%] mt-2 bg-grey-50 shadow-lg rounded-[32px] border p-4 w-96 cursor-pointer"
                ref={mobileMenuRef}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-2 w-full">
                  {renderMobileMenuItems().map((item, index) => (
                    <a
                      key={index}
                      href={item.to}
                      rel="noreferrer"
                      target={
                        item.label === "News" ||
                        item.label === "Blogs" ||
                        item.type === "external"
                          ? "_blank"
                          : "_self"
                      }
                      onClick={() => setshowMobileMenuPopup(false)}
                      className="block w-full !pl-6 !pr-4 !py-3 text-black hover:bg-grey-100 rounded-xl bg-white"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            ref={profileMenuRef}
            className="min-w-max flex justify-end items-center !gap-3 relative cursor-pointer"
            onClick={() => setShowProfilePopup(!showProfilePopup)}
          >
            {createUserDisplayPicture(data?.user?.name, 32)}
            {showProfilePopup && (
              <div
                ref={profileMenuButtonRef}
                className="absolute p-2 bg-white border border-black shadow-sm rounded w-40 right-0 top-12 cursor-pointer"
              >
                <ul className="flex flex-col">
                  {profiles &&
                    profiles?.map((profile) => (
                      <li
                        key={profile.name}
                        className="flex items-center gap-2 p-2 font-bold text-sm text-black rounded cursor-pointer hover:bg-gray-100 transition-all duration-300"
                        onClick={() => {
                          if (profile.name === "Logout") {
                            setLoginOpen(true);
                          } else {
                            handleProfileMenu(profile);
                          }
                        }}
                      >
                        <img src={profile.svg} alt={""} />
                        {profile.name}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex lg:hidden w-1/2 justify-center">
        <GlobalSearch
          globalSearchTerm={globalSearchTerm}
          setGlobalSearchTerm={setGlobalSearchTerm}
        />
      </div>

      <CommonModal
        show={loginOpen}
        handleClose={() => setLoginOpen(false)}
        center={true}
        customDialogModal="max-w-auto rounded-t-2xl overflow-hidden"
        customContentClass="h-80"
        customBodyClass="h-80 bg-white rounded-2xl"
        size="md"
      >
        <div className="bg-white shadow-md px-4 py-3 flex justify-between items-center rounded-t-lg">
          <div className="font-bold text-black text-sm">Logout</div>
          <img
            className="cursor-pointer w-4 h-4"
            src={icons.canceldark}
            alt="Close"
            onClick={() => setLoginOpen(false)}
          />
        </div>

        <div className="h-[calc(100%-110px)]">
          <div className="flex justify-center items-center h-full p-6">
            <div className="text-center text-sm font-bold align-items-center justify-content-center h-auto">
              Are you sure you want to logout?
            </div>
          </div>
          <div className="flex justify-end shadow-[0_-4px_24px_0_rgba(0,0,0,0.1)] !p-3 gap-2 rounded-b-lg bg-white">
            <button
              className="border border-grey-border rounded text-xs font-bold px-6 py-2 bg-white text-black"
              onClick={() => setLoginOpen(false)}
            >
              Cancel
            </button>
            <button
              className="rounded text-xs font-bold pl-3 pr-4 py-2 bg-primary hover:bg-primary-dark text-white flex items-center gap-2"
              onClick={() => handleProfileMenu("Logout")}
            >
              <ArrowLeftStartOnRectangleIcon className="w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};
export default NavBar;
